const sectionSelector = '.capabilities-slider-section';

const DESKTOP_MEDIA = '(min-width: 1200px)';
const ANIMATION_LOCK_DURATION = 1000;
const SWIPE_THRESHOLD = 40;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const capturePointer = (element, pointerId) => {
    if (!element.setPointerCapture) return;

    try {
        element.setPointerCapture(pointerId);
    } catch {}
};

const releasePointer = (element, pointerId) => {
    if (!element.releasePointerCapture) return;

    try {
        element.releasePointerCapture(pointerId);
    } catch {}
};

const sectionCallBack = (sectionElement, query, queryAll) => {
    const slider = query('[data-capabilities-slider]');
    const viewport = query('[data-capabilities-slider-viewport]');
    const track = query('[data-capabilities-slider-track]');
    const cards = queryAll('[data-capabilities-slider-card]');
    const prevButton = query('[data-capabilities-slider-prev]');
    const nextButton = query('[data-capabilities-slider-next]');

    if (!slider || !viewport || !track || cards.length === 0) return;
    if (slider.dataset.capabilitiesSliderInitialized === 'true') return;

    slider.dataset.capabilitiesSliderInitialized = 'true';

    const desktopQuery = window.matchMedia(DESKTOP_MEDIA);

    let activeIndex = 0;
    let isAnimating = false;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let lockTimer = null;
    let resizeFrame = null;
    let resizeTimer = null;

    const getDesktopVisibleCards = () => Math.min(2, cards.length);
    const getVisibleCards = () => (desktopQuery.matches ? getDesktopVisibleCards() : 1);
    const getMaxIndex = () => Math.max(cards.length - getVisibleCards(), 0);

    const getOffsetForIndex = (index) => {
        const card = cards[index];

        return card ? card.offsetLeft : 0;
    };

    const updateButtons = () => {
        const maxIndex = getMaxIndex();
        const atStart = activeIndex <= 0;
        const atEnd = activeIndex >= maxIndex;

        if (prevButton) {
            prevButton.disabled = atStart;
        }

        if (nextButton) {
            nextButton.disabled = atEnd;
        }
    };

    const updateFadedCard = () => {
        let fadedIndex = desktopQuery.matches && activeIndex < getMaxIndex()
            ? activeIndex + getDesktopVisibleCards()
            : -1;

        if (!desktopQuery.matches) {
            const viewportRect = viewport.getBoundingClientRect();
            const visibleWidth = Math.min(viewportRect.right, document.documentElement.clientWidth)
                - viewportRect.left;
            const trackOffset = getOffsetForIndex(activeIndex);

            fadedIndex = cards.findIndex((card, index) => {
                const left = getOffsetForIndex(index) - trackOffset;

                return index > activeIndex
                    && left < visibleWidth
                    && left + card.offsetWidth > visibleWidth + 1;
            });
        }

        cards.forEach((card, index) => {
            card.classList.toggle('is-faded', index === fadedIndex);
        });
    };

    const setTrackPosition = () => {
        track.style.transform = `translate3d(${-getOffsetForIndex(activeIndex)}px, 0, 0)`;
    };

    const updateState = () => {
        activeIndex = clamp(activeIndex, 0, getMaxIndex());
        setTrackPosition();
        updateButtons();
        updateFadedCard();
    };

    const unlock = () => {
        isAnimating = false;
        slider.classList.remove('is-animating');
    };

    const goTo = (requestedIndex) => {
        if (isAnimating) return;

        const nextIndex = clamp(requestedIndex, 0, getMaxIndex());
        if (nextIndex === activeIndex) return;

        activeIndex = nextIndex;
        isAnimating = true;
        slider.classList.add('is-animating');

        updateState();

        window.clearTimeout(lockTimer);
        lockTimer = window.setTimeout(unlock, ANIMATION_LOCK_DURATION);
    };

    const scheduleResizeUpdate = () => {
        if (resizeFrame) return;

        resizeFrame = requestAnimationFrame(() => {
            resizeFrame = null;
            slider.classList.add('is-resizing');
            updateState();

            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                slider.classList.remove('is-resizing');
            }, 80);
        });
    };

    const handlePointerDown = (event) => {
        if (isAnimating) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        pointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        slider.classList.add('is-dragging');

        capturePointer(viewport, pointerId);
    };

    const handlePointerUp = (event) => {
        if (pointerId !== event.pointerId) return;

        const diffX = event.clientX - pointerStartX;
        const diffY = event.clientY - pointerStartY;

        releasePointer(viewport, pointerId);

        pointerId = null;
        slider.classList.remove('is-dragging');

        if (
            Math.abs(diffX) < SWIPE_THRESHOLD
            || Math.abs(diffX) < Math.abs(diffY) * 1.2
        ) {
            return;
        }

        goTo(activeIndex + (diffX < 0 ? 1 : -1));
    };

    const handlePointerCancel = (event) => {
        if (pointerId !== event.pointerId) return;

        releasePointer(viewport, pointerId);

        pointerId = null;
        slider.classList.remove('is-dragging');
    };

    if (prevButton) {
        prevButton.addEventListener('click', () => goTo(activeIndex - 1));
    }

    if (nextButton) {
        nextButton.addEventListener('click', () => goTo(activeIndex + 1));
    }

    viewport.addEventListener('pointerdown', handlePointerDown);
    viewport.addEventListener('pointerup', handlePointerUp);
    viewport.addEventListener('pointercancel', handlePointerCancel);
    viewport.addEventListener('dragstart', (event) => event.preventDefault());
    window.addEventListener('resize', scheduleResizeUpdate, { passive: true });

    if (desktopQuery.addEventListener) {
        desktopQuery.addEventListener('change', scheduleResizeUpdate);
    } else {
        desktopQuery.addListener(scheduleResizeUpdate);
    }

    updateState();
};

export { sectionSelector, sectionCallBack };
